/**
 * GridTile component module.
 * @module shared/components/GridTile
 *
 */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import classNames from 'classnames';
import {fill} from './cssUtils';
import {getBlackWhiteWithOpacity} from '../utils/colors';
import Icon from './Icon';
import Row from './Row';
import './GridTile.css';

const _Title = styled.span`
  text-align: center;
  width: 100px;
  flex-grow: 1;
  margin: 5px;
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)'};
  font-family: ${(props) => props.theme.titleText.name};
  font-size: ${(props) => props.theme.titleText.size};
  line-height: ${(props) => props.theme.titleText.lineHeight};
  ${props => props.theme.neumorphic ? 'text-transform: uppercase' : ''};
`;

const _Content = styled.div`
  position: relative;
  flex-grow: 1;
  padding: 5px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  width: -moz-available;
  //noinspection CssInvalidPropertyValue
  width: -webkit-fill-available;
  background-color: ${(props) => props.theme.tileBackgroundColor};
  ${props => props.theme.dark
    ? 'background-image: linear-gradient(to bottom right, rgba(0, 29, 40, 0.3), rgba(9, 16, 0, 0.3))'
    : 'background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 0.18), rgba(166, 198, 205, 0.18), rgba(255, 255, 255, 0.18))'};
  ${(props) => props.theme.dark ? '' : 'filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.3))'};
  ${props => props.theme.neumorphic ? 'background-image: none' : ''};
  ${props => props.theme.neumorphic
    ? `box-shadow: 2px 2px 5px 2px rgba(0, 0, 0, 0.4),
                   1px 1px 0 0 rgba(0, 0, 0, 0.5),
                   -1px -1px 0 0 rgba(255, 255, 255, 0.2)` : ''};
  border-radius: 12px;
  overflow-y: auto;

  //noinspection CssInvalidPropertyValue
  width: fill-available;
  //noinspection CssInvalidPropertyValue
  width: stretch;
  //min-width: 300px;
`;

const _InnerContent = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 10px;
  width: -moz-available;
  //noinspection CssInvalidPropertyValue
  width: -webkit-fill-available;
  //noinspection CssInvalidPropertyValue
  width: fill-available;
  //noinspection CssInvalidPropertyValue
  width: stretch;
  overflow: auto;
  //min-width: 300px;
`;

const _Divider = styled.hr`
  border-style: solid;
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  ${props => props.theme.neumorphic ? `color: ${props.theme.primaryColor}` : ''};
  ${props => props.theme.neumorphic ? 'opacity: 1' : ''};
  border-width: 1px;
  ${fill()}
  margin: 5px;
`;

const _Tile = styled.div`
  padding: 5px;
  box-sizing: border-box;
  //margin: 10px;
  & .react-resizable-handle.react-resizable-handle-se {
    z-index: 2;
    ${props => props.static && 'display: none' || ''};
  }
`;

export const _Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 10px;
  top: 0;
  left: 0;
  z-index: 1;
`;

/**
 * Tile component
 * @name GridTile
 */
const GridTile = React.forwardRef((props, ref) => {
  const {
    title,
    children,
    editing,
    icon,
    className,
    maximize,
    onHide,
    ...others
  } = props;

  const [draggable, setDraggable] = useState(true);

  return (
    <_Tile
      ref={ref}
      onMouseOver={(e) => {
        const cancelDrag = /cb-gridtile-hide|cb-gridtile-resize/.test(e?.target?.className);
        setDraggable(!cancelDrag);
      }}
      className={classNames(className, {
        'cb-gridtile': true,
        'cb-gridtile-editing': editing,
        'cb-gridtile-draggable': draggable,
      })}
      {...others}>
      <_Content>
        {title ? (
          <>
            <Row justifySubRow={'space between'} wrap={false}>
              {icon || <div style={{width: '20px'}}>&nbsp;</div>}
              <_Title>{title}</_Title>
              {maximize && !editing ? (
                <Icon
                  width={'20px'}
                  height={'20px'}
                  svg={editing ? '' : 'maximize'}
                  action={maximize}
                />
              ) : <div style={{width: '20px'}}>&nbsp;</div>}
            </Row>
            <_Divider/>
          </>
        ) : null}
        <_InnerContent className="cb-gridtile-inner">
          {children}
        </_InnerContent>
        {editing ? (
          <_Overlay>
            <Icon
              className="cb-gridtile-move"
              svg="tileMove"
              width="40px"
              height="40px"
              color={getBlackWhiteWithOpacity(0.7)}
            />
            <Icon
              className="cb-gridtile-hide"
              svg="tileHide"
              width="40px"
              height="40px"
              action={onHide}
              color={getBlackWhiteWithOpacity(0.7)}
            />
          </_Overlay>
        ) : null}
      </_Content>
    </_Tile>
  );
});

export default GridTile;

GridTile.displayName = 'GridTile';

GridTile.propTypes = {
  /**
   * Title of Tile
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The layout for the grid. All numbers are 'Grid Units'. A "Grid Unit" is 1 Page Column for width and Page Row Height.
   */
  'data-grid': PropTypes.shape({
    /**
     * The X (Horizontal) Position in the Grid
     */
    x: PropTypes.number.isRequired,
    /**
     * The Y (Vertical) Position in the Grid
     */
    y: PropTypes.number.isRequired,
    /**
     * The Width in Grid Units of the Tile. If greater then the Width of the Grid will be set to the Width of the Grid.
     */
    w: PropTypes.number.isRequired,
    /**
     * The Height in Grid Units of the Tile.
     */
    h: PropTypes.number.isRequired,
    /**
     * The Minimum Width of the Tile in Grid Units. If less then the Width of the Grid will be set to the Width of the Grid.
     */
    minW: PropTypes.number.isRequired,
    /**
     * The Minimum Height of the Tile in Grid Units
     */
    maxW: PropTypes.number.isRequired,
  }),
  /**
   * React Element children
   */
  children: PropTypes.node,
  /**
   * Indicates if layout is being edited and should dim the content.
   */
  editing: PropTypes.bool,
  /**
   * If true hides the resize handles completely
   */
  static: PropTypes.bool,
  /**
   * Optional function to maximize
   */
  maximize: PropTypes.func,
  /**
   * Icon to display on left side
   */
  icon: PropTypes.node,

  /**
   * onHide handles the click event on the close icon
   */
  onHide: PropTypes.func,

  /**
   * className string
   */
  className: PropTypes.string,
};

GridTile.defaultProps = {
  onHide: () => {},
  icon: null,
};
